import React, { useState, useRef, useImperativeHandle } from 'react';
import { Alert, Button, Divider, Form, message, Modal, Popconfirm, Radio, Tooltip } from 'antd';
import { useMutation } from '@apollo/client'; 
import courseFormUtils from './courseFormUtils';
import useSyncObjectRelationships from '../../gql/hooks/useSyncObjectRelationships';
import PUBLISH_COURSE_MUTATION from '../../gql/mutations/PUBLISH_COURSE_MUTATION';
import UNPUBLISH_COURSE_MUTATION from '../../gql/mutations/UNPUBLISH_COURSE_MUTATION';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags'; 
import SelectorTable from '../../components/SelectorTable/SelectorTable';
import useLearningObjectCourseMutations from '../../gql/hooks/useLearningObjectCourseMutations';
import usePatchCourse from '../../gql/hooks/usePatchCourse';
import { QuestionCircleOutlined } from '@ant-design/icons';

const fieldNames = [ 'published', 'isSequential', 'learningObjects', 'resources' ];

const CourseTabContent = ( { form, initialValues, isSavingCallback, reloadCourseCallback, courseId, ref } ) => {

    const resourceSelectorRef = useRef();
    const objectSelectorRef = useRef();
    const [ isSaving, setIsSaving ] = useState( false );
    const [ sync ] = useSyncObjectRelationships();
    const [ publishCourse ] = useMutation( PUBLISH_COURSE_MUTATION );
    const [ unpublishCourse ] = useMutation( UNPUBLISH_COURSE_MUTATION );
    const { saveCourse } = usePatchCourse();
    const [ showResources, sequentialAllowed ] = useFeatureFlags( [ flags.RESOURCES, flags.SEQUENTIAL_COURSES ] );
    const [ confirmOpen, setConfirmOpen ] = useState( false );

    const [ messageApi, contextHolder ] = message.useMessage();

    const {
        addLearningObjects,
        deleteLearningObjects,
        enableLearningObjects,
        disableLearningObjects,
        sortLearningObjects,
    } = useLearningObjectCourseMutations( courseId );

    useImperativeHandle( ref, () => ( {
        isModified() {
            return form.isFieldsTouched( fieldNames );
        },
        reset() {
            handleReset();
        },
        showSaveConfirm: () => {setConfirmOpen( true );}
    } ) );

    async function saveTab() {

        let hasError = false;
        const unsuccessfulFields = [  ];

        if ( form.isFieldTouched( 'published' ) && form.getFieldValue( 'published' ) === false ) {
            try {
                const response = await unpublishCourse( {
                    variables: { 
                        courseID: form.getFieldValue( 'id' )
                    }
                } );

                if ( !response.data.result.success ) {
                    throw new Error( response );
                }
                
            } catch ( e ) {
                console.error( 'Failed to unpublish course', e );
                messageApi.error( 'Failed to unpublish course. Your changes have not been saved' ); 
                return false;
            }
        }

        if ( form.isFieldTouched( 'learningObjects' ) ) {

            const originalObjects = initialValues.learningObjects;
            const newObjects = form.getFieldValue( 'learningObjects' );

            const addIds = newObjects.filter( n => !originalObjects.some( o => o.id === n.id ) ).map( i => i.id );
            const deleteIds = originalObjects.filter( o => !newObjects.some( n => n.id === o.id ) ).map( i => i.id );
            const enableIds = newObjects.filter( n => {
                const original = originalObjects.find( o => o.id === n.id );
                return original && original.isEnabled === false && n.isEnabled === true;
            } ).map( i => i.id );
            const disableIds = newObjects.filter( n => {
                const original = originalObjects.find( o => o.id === n.id );
                return original && original.isEnabled === true && n.isEnabled === false;
            } ).map( i => i.id );

            let errors = [];
            const addResult = await addLearningObjects( addIds );
            if ( addResult.errors.length ) {
                errors.push( `Failed to add ${ addResult.errors.length } learning objects` );
                console.error( 'Failed to add learning objects:', addResult.errors );
            }

            const deleteResult = await deleteLearningObjects( deleteIds );
            if ( deleteResult.errors.length ) {
                errors.push( `Failed to remove ${ deleteResult.errors.length } learning objects` );
                console.error( 'Failed to remove learning objects:', deleteResult.errors );
            }

            const enableResult = await enableLearningObjects( enableIds );
            if ( enableResult.errors.length ) {
                errors.push( `Failed to unhide ${ enableResult.errors.length } learning objects` );
                console.error( 'Failed to unhide learning objects:', enableResult.errors );
            }

            const disableResult = await disableLearningObjects( disableIds );
            if ( disableResult.errors.length ) {
                errors.push( `Failed to hide ${ disableResult.errors.length } learning objects` );
                console.error( 'Failed to hide learning objects:', disableResult.errors );
            }

            if ( newObjects.length > 0 && ( newObjects.length !== originalObjects.length || !newObjects.every( ( n, i ) => n.id === originalObjects[ i ].id ) ) ) {
                const sortResult = await sortLearningObjects( newObjects.map( o => o.id ) );
                if ( sortResult.errors.length ) {
                    errors.push( 'Failed to reorder learning objects' );
                    console.error( 'Failed to reorder learning objects:', sortResult.errors );
                }
            }

            if ( errors.length ) {
                errors.forEach( e => messageApi.error( e ) );
                hasError = true;
                unsuccessfulFields.push( 'learningObjects' );
            }
        }

        // any other basic prop changes can go here
        if ( form.isFieldTouched( 'isSequential' ) && sequentialAllowed ) {
            const { success, response } = await saveCourse( courseFormUtils.getCourseSaveData( form ) );

            if ( !success ) {
                console.error( 'Failed to save sequential completion', response );
                messageApi.error( 'Failed to save sequential completion' );
                hasError = true;
                unsuccessfulFields.push( 'isSequential' );
            }
        }

        if ( form.isFieldTouched( 'resources' ) ) {
            const relatedResourceIds = form.getFieldValue( 'resources' ).map( r => r.id );

            try {
                await sync( { variables: { input: {
                    objectType: form.getFieldValue( 'type' ),
                    objectId: form.getFieldValue( 'id' ),
                    relatedResourceType: 'resources',
                    relatedResourceIds
                } } } );
                resourceSelectorRef.current?.reset();
                
            } catch ( error ) { 
                console.error( 'Error saving resources', error );  
                messageApi.error( 'Failed to save resources' );
                hasError = true;
                unsuccessfulFields.push( 'resources' );
            } 
        }

        if ( form.isFieldTouched( 'published' ) && form.getFieldValue( 'published' ) === true ) {
            if ( hasError ) {
                messageApi.error( 'Skipped publish step' );
                unsuccessfulFields.push( 'published' );
            } else {
                try {
                    const response = await publishCourse( {
                        variables: { 
                            courseID: form.getFieldValue( 'id' )
                        }
                    } );
    
                    if ( response.data.result.success ) {
                        form.setFieldsValue( { hasRevision: true } );  
                        
                    } else {
                        throw new Error( response );
                    }
    
                } catch ( e ) {
                    console.error( e );
                    messageApi.error( 'Failed to publish course' );
                    hasError = true;
                    unsuccessfulFields.push( 'published' );
                }
            }
        }

        if ( !hasError ) {
            messageApi.success( 'Course content successfully saved' );
        }
        
        await reloadCourseCallback( unsuccessfulFields );

        return !hasError;
    }

    const handleSave = async () => {
        setIsSaving( true );
        isSavingCallback( true );

        // only a fully successful save lets the parent move on to the tab the user was heading to
        let saved = false;

        await form.validateFields( fieldNames )
            .then( async () => { saved = await saveTab(); } )
            .catch( e => {
                messageApi.error( 'Invalid data' );
                console.error( e );
            } )
            .finally( () => {
                setIsSaving( false );
                isSavingCallback( false, saved );
                setConfirmOpen( false );

            } );

    };

    const handleReset = () => {
        form.resetFields( fieldNames );
        resourceSelectorRef.current?.reset();
        objectSelectorRef.current?.reset();
        isSavingCallback( false );
        setConfirmOpen( false );
    };

    return (
        <div className="content-tab tab-main-columns">
            { contextHolder }
            <div className="left">
                <Form.Item 
                    name='published' 
                    label={ <b>Publishing <Tooltip title='Unpublished courses are not visible to the learning user'><QuestionCircleOutlined /></Tooltip></b> }
                    >
                    <Radio.Group disabled={ isSaving } optionType="button" buttonStyle="solid" options={ [ { value: true, label: 'Publish' }, { value: false, label: 'Unpublish' } ] } />

                </Form.Item>

                { sequentialAllowed && <Form.Item 
                    name='isSequential' 
                    label={ <b>Sequential Completion <Tooltip title='Users must complete learning objects in the defined order if Sequential Completion is enabled.'><QuestionCircleOutlined /></Tooltip></b> }
                    >
                    <Radio.Group disabled={ isSaving } optionType="button" buttonStyle="solid" options={ [ { value: false, label: 'Non-Sequential' }, { value: true, label: 'Sequential' } ] } />

                </Form.Item> }

                <br/>

                <Form.Item name='learningObjects' label={ <b>Learning Objects <Tooltip title='Select learning objects to show on this course. Reorder selected objects and hide those that should no longer be shown.'><QuestionCircleOutlined /></Tooltip></b> } 
                    dependencies={ [ 'published' ] }
                    help={ initialValues.published ? <Alert style={ { marginTop: 10 } } type='info' message='Learning objects can not be changed while course is published' /> : undefined }
                    rules={ [ 
                        ( { getFieldValue } ) => ( {
                            validator( _, value ) {
                                if ( ( !value || value.filter( i => i.isEnabled ).length === 0 ) && getFieldValue( 'published' ) === true ) {
                                    return Promise.reject( new Error( 'A published course must have at least one learning object' ) );
                                }
                                return Promise.resolve();
                            }
                        } )
                    ] }
                >
                    <SelectorTable.LearningObjects
                        ref={ objectSelectorRef }
                        disabled={ initialValues.published || isSaving }
                    
                    />
                </Form.Item>

                { showResources && 
                    <>
                        <Divider />
                        <Form.Item 
                            name='resources' 
                            label={ <b>Resources <Tooltip title='Select resources to show on this course.'><QuestionCircleOutlined /></Tooltip></b> }
                        >
                            <SelectorTable.Resources
                                ref={ resourceSelectorRef }
                                disabled={ isSaving }
                            />
                        </Form.Item>
                    </>
                }
            </div>

            <div className="right">
                <Form.Item shouldUpdate noStyle>
                    { () => {
                        const hasErrors = form.getFieldsError( fieldNames ).some( ( { errors } ) => errors.length );
                        const isDirty = form.isFieldsTouched( fieldNames );
                        return <> 
                            <Button
                                type="primary"
                                block
                                disabled={ !isDirty || isSaving || hasErrors }
                                onClick={ handleSave }
                            >
                                Save
                            </Button> 
                            <Popconfirm
                                title='Undo changes?'
                                description='Discard unsaved changes to this tab?'
                                onConfirm={ handleReset }
                                okText='Discard changes'
                                cancelText='Cancel'
                            >
                                <Button
                                    type='default'
                                    block
                                    disabled={ !isDirty }
                                >
                                    Reset
                                </Button>
                            </Popconfirm>    
                        </>;
                    } }
                </Form.Item>
            </div>

            { /* really this should be in the parent */ }
            <Modal
                title="Save Changes?" 
                open={ confirmOpen } 
                onOk={ handleSave } 
                onCancel={ handleReset }
                okText="Save"
                cancelText="Discard changes"
                confirmLoading={ isSaving }
                cancelButtonProps={ { disabled: isSaving } }
            >
                <p>You currently have unsaved changes in this tab.</p>
                <p>Save changes before proceeding?</p>
            </Modal>
        </div>
    );
};

export default CourseTabContent;
